import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@punchup/utils/cn";

const buttonVariants = cva(
  "inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-white transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 dark:ring-offset-neutral-950 dark:focus-visible:ring-neutral-300",
  {
    variants: {
      variant: {
        punchup: "btn-outlined text-primary border-primary hover:bg-slate-100",
        punchupSolid: "bg-primary font-semibold text-white hover:bg-primary/90",
        punchupInverted: "btn flex items-center shadow-sm disabled:opacity-100",
        punchupSecondary:
          "text-primary hover:bg-slate-100 border-transparent border-2 bg-ivory text-black font-base",
        punchupSecondarySelected:
          "text-primary hover:bg-slate-100 border-primary border-2 bg-ivory text-black font-base bg-white",
        default:
          "bg-neutral-900 text-neutral-50 hover:bg-neutral-900/90 dark:bg-neutral-50 dark:text-neutral-900 dark:hover:bg-neutral-50/90",
        grey: "bg-slate-gray-500 text-neutral-50 hover:bg-neutral-900/90 dark:bg-neutral-50 dark:text-neutral-900 dark:hover:bg-neutral-50/90",
        destructive:
          "bg-red-500 text-neutral-50 hover:bg-red-500/90 dark:bg-red-900 dark:text-neutral-50 dark:hover:bg-red-900/90",
        outline:
          "border border-neutral-200 bg-white hover:bg-neutral-100 hover:text-neutral-900 dark:border-neutral-800 dark:bg-neutral-950 dark:hover:bg-neutral-800 dark:hover:text-neutral-50",
        secondary:
          "bg-neutral-100 text-neutral-900 hover:bg-neutral-100/80 dark:bg-neutral-800 dark:text-neutral-50 dark:hover:bg-neutral-800/80",
        ghost:
          "hover:bg-neutral-100 hover:text-neutral-900 dark:hover:bg-neutral-800 dark:hover:text-neutral-50",
        link: "text-neutral-900 underline-offset-4 hover:underline dark:text-neutral-50",
        punchupLink: "text-primary hover:text-secondary-orange",
        dashboardButton:
          "hover:bg-slate-gray-200 hover:border-slate-gray-400 text-slate-gray-700",
        dashboardButtonActive: "text-primary",
        dashboardMobileButton: "text-slate-gray-700 rounded-none",
        dashboardMobileButtonActive:
          "text-blue-600 disabled border-b-2 border-b-blue-600 rounded-none",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10",
      },
    },
    // After `size` so its radius beats the sm/lg sizes' rounded-md under
    // tailwind-merge — punchupSolid keeps one radius at every size.
    compoundVariants: [{ variant: "punchupSolid", className: "rounded-[8px]" }],
    defaultVariants: {
      variant: "punchup",
      size: "default",
    },
  }
);

export interface ButtonProps
  extends
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  }
);
Button.displayName = "Button";

export { Button, buttonVariants };
